import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  AfterViewInit,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { ProfileUpdateRequestImpl } from "shared/cubiq-model-multi-asset";
import { BfxUserSettingsService, BfxUserSettingsComponentState } from "./bfx-user-settings.service";
import {
  BfxUserSettingsPageComponent,
  BfxUserSettingsValidityState,
} from "./bfx-user-settings-page.component";
import {
  ProfileSettingKeys,
  SystemConfigPromiseService,
  ProfilePromiseActionService,
  UserPromiseLookupService,
  SysFeatures,
  ActiveUser,
} from "shared/cubiq-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { IBfxUserSettingsPageSubComponent } from "app/bfx-user-settings/bfx-user-settings-page.component";
import { BfxUserSettingsComponent } from "app/bfx-user-settings/bfx-user-settings.component";
import { BfxOrderBlotterSettingsComponent } from "app/bfx-user-settings/bfx-order-blotter-settings.component";
import { BfxInputControlStateEnum } from "../bfx-interface-controls/models/bfx-interface-controls.enums";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

/**
 * Preferences model structure managed by this view
 */
export type OrderBlotterSavedSettingType = {
  marketBar: string;
  pauseRatesModel: number;
  tempColorFullModel: string;
  pinTriggeredOrdersModel: string;
  intentStylingEnabled: string;
};

@Component({
  selector: "bfx-order-blotter-settings-preferences",
  templateUrl: "./html/bfx-order-blotter-settings-preferences.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxOrderBlotterSettingsPreferencesComponent
  implements IBfxUserSettingsPageSubComponent<BfxOrderBlotterSettingsComponent>, OnInit, OnDestroy
{
  public activeSubComponent: number = 0;
  public categoryComponent: BfxOrderBlotterSettingsComponent = null;
  public parentComponent: BfxUserSettingsComponent = null;
  public marketBar: boolean = true;
  public tempColorFull: boolean = true;
  public disableTempColorFull: boolean = false;
  public panelHeight: number = 0;
  private tempValueToUpdateActiveUser: string;
  private tempLadderValueToUpdateActiveUser: string;
  public pauseRatesModel: number = 30;
  public pauseRatesModelExternalState = BfxInputControlStateEnum.INACTIVE;
  public pauseRatesStep: number = 1;
  public ready: boolean = false;
  public pinTriggeredOrders: boolean = true;
  public supportsAggregation: boolean = false;
  public intentStylingEnabled: boolean = false;
  public panelStyle = null;

  private loadedData: {
    pauseRatesModel: number;
  } = {
    pauseRatesModel: null,
  };

  public ladderPreferenceData = {
    supportsAggregation: null,
  };

  public preferenceData: OrderBlotterSavedSettingType = {
    marketBar: null,
    pauseRatesModel: null,
    tempColorFullModel: null,
    pinTriggeredOrdersModel: null,
    intentStylingEnabled: null,
  };
  constructor(
    public userSettingsService: BfxUserSettingsService,
    public profileService: ProfilePromiseActionService,
    public userService: UserPromiseLookupService,
    public element: ElementRef,
    public translationService: TranslateService,
    private sysConfigService: SystemConfigPromiseService,
    private cd: ChangeDetectorRef,
  ) {}

  public onTabActivated(event: any): void {}

  enableMarketBarToggle($event: any) {
    this.marketBar = !this.marketBar;
    this.parentComponent.validate(this.categoryComponent);
  }

  enableTempColorFull($event: any) {
    this.tempColorFull = !this.tempColorFull;
    this.parentComponent.validate(this.categoryComponent);
  }

  enablePinTriggeredOrders($event: any) {
    this.pinTriggeredOrders = !this.pinTriggeredOrders;
    this.parentComponent.validate(this.categoryComponent);
  }

  enableSupportsAggregation($event: any) {
    this.supportsAggregation = !this.supportsAggregation;
    this.parentComponent.validate(this.categoryComponent);
  }

  toggleIntentStyling($event: any) {
    this.intentStylingEnabled = !this.intentStylingEnabled;
    this.parentComponent.validate(this.categoryComponent);
  }

  pauseRatesBtnClick($event) {
    this.parentComponent.validate(this.categoryComponent);
  }

  pauseRatesModelonBlur($event) {
    if (this.detectChanges()) {
      this.parentComponent.validate(this.categoryComponent);
    }
  }

  private detectChanges(): boolean {
    return this.pauseRatesModel != this.loadedData.pauseRatesModel;
  }

  public initialize(): void {
    let activeUser: ActiveUser = this.parentComponent.activeUser;
    if (
      activeUser.profileSettings &&
      activeUser.profileSettings.has(ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences)
    ) {
      let savedSettings = JSON.parse(
        activeUser.profileSettings.get(
          ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences,
        ),
      );
      this.sysConfigService.getSystemConfig(SysFeatures.blotterMarketBar).then((value) => {
        if (value == "Y") {
          this.marketBar = true;
          if (savedSettings.hasOwnProperty("marketBar") && savedSettings.marketBar == "y") {
            this.marketBar = true;
          } else {
            this.marketBar = false;
          }
        } else {
          this.marketBar = false;
        }

        if (
          savedSettings.hasOwnProperty("pauseRatesModel") &&
          savedSettings.pauseRatesModel !== null &&
          savedSettings.pauseRatesModel > 0
        ) {
          this.pauseRatesModel = savedSettings.pauseRatesModel;
          this.loadedData.pauseRatesModel = savedSettings.pauseRatesModel;
          this.pauseRatesModelExternalState = BfxInputControlStateEnum.VALID;
        } else {
          this.pauseRatesModel = 30;
          this.loadedData.pauseRatesModel = 30;
          this.pauseRatesModelExternalState = BfxInputControlStateEnum.INACTIVE;
        }
        if (savedSettings.hasOwnProperty("tempColorFullModel")) {
          if (savedSettings.tempColorFullModel == "y") {
            this.tempColorFull = true;
          } else {
            this.tempColorFull = false;
          }
        }
        if (savedSettings.hasOwnProperty("pinTriggeredOrdersModel")) {
          if (savedSettings.pinTriggeredOrdersModel == "y") {
            this.pinTriggeredOrders = true;
          } else {
            this.pinTriggeredOrders = false;
          }
        }
        this.intentStylingEnabled = savedSettings.intentStylingEnabled === "y";

        if (
          activeUser.profileSettings &&
          activeUser.profileSettings.has(
            ProfileSettingKeys.userProfileSettingsLadderOrdersPreferences,
          )
        ) {
          let savedLadderSettings = this.ladderPreferenceData;
          savedLadderSettings = JSON.parse(
            activeUser.profileSettings.get(
              ProfileSettingKeys.userProfileSettingsLadderOrdersPreferences,
            ),
          );

          if (
            savedLadderSettings.hasOwnProperty("supportsAggregation") &&
            savedLadderSettings.supportsAggregation === true
          ) {
            this.supportsAggregation = savedLadderSettings.supportsAggregation;
          }
        }
        setTimeout(() => {
          this.ready = true;
          this.cd.markForCheck();
        }, 100);
      });
    } else {
      setTimeout(() => {
        this.ready = true;
        this.cd.markForCheck();
      }, 200);
    }
  }

  ngOnInit() {
    let componentState: BfxUserSettingsComponentState = new BfxUserSettingsComponentState();
    this.onComponentStateChanged(componentState);
  }

  ngOnDestroy() {}

  public validate(): Promise<BfxUserSettingsValidityState> {
    return new Promise<BfxUserSettingsValidityState>((res, reject) => {
      let state;

      if (this.pauseRatesModel !== 0) {
        state = BfxUserSettingsValidityState.VALID;
      } else {
        state = BfxUserSettingsValidityState.INVALID;
      }

      //logger.out.info(`${this.constructor.name}.validate() valid:BfxUserSettingsValidityState.${BfxUserSettingsValidityState[state]}`);

      res(state);
    });
  }

  public reset(): Promise<void> {
    return new Promise<void>((res, reject) => {
      let activeUser: ActiveUser = this.parentComponent.activeUser;
      if (
        activeUser.profileSettings &&
        activeUser.profileSettings.has(
          ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences,
        )
      ) {
        let savedSettings = this.preferenceData;
        savedSettings = JSON.parse(
          activeUser.profileSettings.get(
            ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences,
          ),
        );

        if (savedSettings.hasOwnProperty("marketBar") && savedSettings.marketBar == "y") {
          this.marketBar = true;
        } else {
          this.marketBar = false;
        }

        if (savedSettings.hasOwnProperty("tempColorFullModel")) {
          if (savedSettings.tempColorFullModel == "y") {
            this.tempColorFull = true;
          } else {
            this.tempColorFull = false;
          }
        }

        if (savedSettings.hasOwnProperty("pinTriggeredOrdersModel")) {
          if (savedSettings.pinTriggeredOrdersModel == "y") {
            this.pinTriggeredOrders = true;
          } else {
            this.pinTriggeredOrders = false;
          }
        }
        this.intentStylingEnabled = savedSettings.intentStylingEnabled === "y";

        if (
          savedSettings.hasOwnProperty("pauseRatesModel") &&
          savedSettings.pauseRatesModel !== null &&
          savedSettings.pauseRatesModel > 0
        ) {
          this.pauseRatesModel = savedSettings.pauseRatesModel;
          this.pauseRatesModelExternalState = BfxInputControlStateEnum.VALID;
        } else {
          this.pauseRatesModel = null;
          this.pauseRatesModelExternalState = BfxInputControlStateEnum.INACTIVE;
        }

        if (
          activeUser.profileSettings &&
          activeUser.profileSettings.has(
            ProfileSettingKeys.userProfileSettingsLadderOrdersPreferences,
          )
        ) {
          let savedLadderSettings = this.ladderPreferenceData;
          savedLadderSettings = JSON.parse(
            activeUser.profileSettings.get(
              ProfileSettingKeys.userProfileSettingsLadderOrdersPreferences,
            ),
          );

          if (
            savedLadderSettings.hasOwnProperty("supportsAggregation") &&
            savedLadderSettings.supportsAggregation === true
          ) {
            this.supportsAggregation = savedLadderSettings.supportsAggregation;
          }
        }
      }
      this.cd.detectChanges();
      res();
    });
  }

  public save(request: ProfileUpdateRequestImpl): void {
    let savedTempSetting: CubiqTS.ProfileSettingImpl = request.settings.find((setting) => {
      return (
        setting.profileSettingKey == ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences
      );
    });

    let valuesToSave = [];

    let objToSave = this.preferenceData;
    objToSave.marketBar = this.marketBar === true ? "y" : "n";
    objToSave.tempColorFullModel =
      this.tempColorFull === true && this.disableTempColorFull === false ? "y" : "n";
    objToSave.pinTriggeredOrdersModel = this.pinTriggeredOrders === true ? "y" : "n";
    objToSave.pauseRatesModel =
      this.pauseRatesModel !== null && this.pauseRatesModel > 0 ? this.pauseRatesModel : null;
    objToSave.intentStylingEnabled = this.intentStylingEnabled === true ? "y" : "n";

    this.loadedData.pauseRatesModel = this.pauseRatesModel;

    valuesToSave.push(objToSave);
    let settingObject: CubiqTS.ProfileSettingImpl = new CubiqTS.ProfileSettingImpl();
    settingObject.profileSettingKey = ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences;
    this.tempValueToUpdateActiveUser = JSON.stringify(objToSave);
    settingObject.profileSettingValue = this.tempValueToUpdateActiveUser;
    if (savedTempSetting === undefined) {
      settingObject.profileSettingValue = this.tempValueToUpdateActiveUser;
      request.settings.push(settingObject);
    } else {
      savedTempSetting.profileSettingValue = this.tempValueToUpdateActiveUser;
    }

    let savedLadderSetting = request.settings.find((setting) => {
      return (
        setting.profileSettingKey == ProfileSettingKeys.userProfileSettingsLadderOrdersPreferences
      );
    });

    let ladderValuesToSave = [];

    let ladderObjToSave = this.ladderPreferenceData;
    ladderObjToSave.supportsAggregation = this.supportsAggregation;
    ladderValuesToSave.push(ladderObjToSave);
    let ladderSettingObject: CubiqTS.ProfileSettingImpl = new CubiqTS.ProfileSettingImpl();
    ladderSettingObject.profileSettingKey =
      ProfileSettingKeys.userProfileSettingsLadderOrdersPreferences;
    this.tempLadderValueToUpdateActiveUser = JSON.stringify(ladderObjToSave);
    ladderSettingObject.profileSettingValue = this.tempLadderValueToUpdateActiveUser;
    if (savedLadderSetting === undefined) {
      ladderSettingObject.profileSettingValue = this.tempLadderValueToUpdateActiveUser;
      request.settings.push(ladderSettingObject);
    } else {
      savedLadderSetting.profileSettingValue = this.tempLadderValueToUpdateActiveUser;
    }
  }

  public postReset(): void {}

  public postSave(request: ProfileUpdateRequestImpl): void {
    let activeUser: ActiveUser = this.parentComponent.activeUser;
    if (activeUser.profileSettings) {
      activeUser.profileSettings.set(
        ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences,
        this.tempValueToUpdateActiveUser,
      );
      activeUser.profileSettings.set(
        ProfileSettingKeys.userProfileSettingsLadderOrdersPreferences,
        this.tempLadderValueToUpdateActiveUser,
      );
    }
  }

  public onComponentStateChanged(componentState: BfxUserSettingsComponentState): void {
    // this.panelHeight =
    //   componentState.componentBounds.height - this.element.nativeElement.offsetTop - 120;
    // this.panelStyle = {
    //   height: `${this.panelHeight}px`,
    //   "max-height": this.panelHeight + 3 + "px",
    // };
  }

  public hasDataChanged(): boolean {
    return null;
  }
}
